import { ActionFormValues } from 'react-components/dialog-forms/components/useNeedActionFormtypes';
import { FieldPath } from 'react-hook-form';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import STATE_OPTIONS from 'redux/constants/stateOptions';

/**
 * Builds a typed FormBuilder field path for one resource row.
 *
 * @param resourceDetailsIndex - Index in `resourceDetails`.
 * @param field - Field on the resource row.
 * @returns RHF path for FormBuilder `name`.
 */
function resourceFieldPath(
  resourceDetailsIndex: number,
  field: string
): FieldPath<ActionFormValues> {
  return `resourceDetails.${resourceDetailsIndex}.${field}` as FieldPath<ActionFormValues>;
}

/**
 * FormBuilder config for one need-action resource referral row.
 *
 * @param resourceDetailsIndex - Index in `resourceDetails` array.
 * @param disableResourceName - Whether the selected resource name is read-only.
 * @returns Form config bound to that row.
 */
export function buildNeedActionResourceFormConfig(
  resourceDetailsIndex: number,
  disableResourceName = false
): FormConfig<ActionFormValues> {
  const stateOptions = STATE_OPTIONS;

  return [
    {
      type: 'input',
      name: resourceFieldPath(resourceDetailsIndex, 'resourceName'),
      label: 'Resource name',
      input: 'text',
      disabled: disableResourceName,
      props: {
        trackingName: 'resource-name-input',
        required: true,
        fullWidth: true,
      },
    },
    {
      type: 'input',
      name: resourceFieldPath(resourceDetailsIndex, 'contactName'),
      label: 'Contact name',
      input: 'text',
      props: { fullWidth: true, trackingName: 'contact-name-input' },
    },
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: resourceFieldPath(resourceDetailsIndex, 'phoneNumber'),
          label: 'Phone number',
          input: 'phone',
          props: { fullWidth: true, trackingName: 'resource-phone-input' },
        },
        {
          type: 'input',
          name: resourceFieldPath(resourceDetailsIndex, 'url'),
          label: 'Website URL',
          input: 'text',
          props: { fullWidth: true, trackingName: 'resource-url-input' },
        },
      ],
    },
    {
      type: 'input',
      name: resourceFieldPath(resourceDetailsIndex, 'email'),
      label: 'Email',
      input: 'text',
      props: { fullWidth: true, trackingName: 'resource-email-input' },
    },
    {
      type: 'input',
      name: resourceFieldPath(resourceDetailsIndex, 'streetAddress'),
      label: 'Address',
      input: 'text',
      props: { fullWidth: true, trackingName: 'street-address-input' },
    },
    {
      type: 'group',
      items: [
        {
          type: 'input',
          name: resourceFieldPath(resourceDetailsIndex, 'city'),
          label: 'City',
          input: 'text',
          props: { fullWidth: true, trackingName: 'city-input' },
        },
        {
          type: 'input',
          name: resourceFieldPath(resourceDetailsIndex, 'state'),
          label: 'State',
          input: 'select',
          props: {
            fullWidth: true,
            trackingName: 'state-select',
            options: Object.entries(stateOptions).map(([label, value]) => ({
              key: label.concat('-', value),
              label: `${label} - ${value}`,
              value,
            })),
          },
        },
        {
          type: 'input',
          name: resourceFieldPath(resourceDetailsIndex, 'zipcode'),
          label: 'Zip code',
          input: 'text',
          props: {
            fullWidth: true,
            trackingName: 'zipcode-input',
            characterLimit: 5,
            hideCharacterCount: true,
          },
        },
      ],
    },
  ] as FormConfig<ActionFormValues>;
}
